import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money, periodLabel } from "@/lib/format";
import { toast } from "sonner";
import { CheckCircle2, Loader2, X, AlertTriangle, Mail } from "lucide-react";

/**
 * One-click monthly finalise wizard.
 * Walks admin through: health check → reconcile preview → generate payouts → email all partners.
 * Each stage is advanced manually (confirm before mutating) so a worried admin can abort.
 */
export default function FinaliseMonthWizard({ period, onClose, onDone }) {
  // Persist mid-flow progress to localStorage keyed by period so an
  // admin who accidentally closes the modal (or has to pop out for a
  // phone call) resumes exactly where they left off instead of
  // restarting from the health check. Cleared on stage 4 (Done).
  const storageKey = `finalise-wizard:${period}`;
  const persisted = (() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || "null"); }
    catch { return null; }
  })();

  const [stage, setStage] = useState(persisted?.stage ?? 0); // 0=health, 1=preview, 2=generate, 3=email, 4=done
  const [strip, setStrip] = useState(null);
  const [preview, setPreview] = useState(null);
  const [generated, setGenerated] = useState(persisted?.generated ?? null);
  const [emailResult, setEmailResult] = useState(persisted?.emailResult ?? null);
  const [busy, setBusy] = useState(false);
  const [resumeNotice, setResumeNotice] = useState(!!persisted && persisted.stage > 0 && persisted.stage < 4);

  // Pull fresh health + preview when the wizard opens
  useEffect(() => {
    (async () => {
      try {
        const [h, p] = await Promise.all([
          api.get("/commissions/ledger/health-strip"),
          api.get(`/commissions/payouts/reconcile-preview/${period}`),
        ]);
        setStrip(h.data);
        setPreview(p.data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Wizard setup failed");
      }
    })();
  }, [period]);

  // Persist on every stage/payload change — tiny payload, no debounce needed
  useEffect(() => {
    try {
      if (stage === 4) {
        // Clear once finalised so re-opening the wizard for the same
        // period next month starts clean. (Period changes rotate the
        // storage key automatically.)
        localStorage.removeItem(storageKey);
      } else {
        localStorage.setItem(storageKey, JSON.stringify({ stage, generated, emailResult }));
      }
    } catch { /* storage disabled — ignore */ }
  }, [stage, generated, emailResult, storageKey]);

  const totalEx = (preview?.rows || []).reduce((s, r) => s + Number(r.amount_ex_gst || 0), 0);
  const partnerCount = (preview?.rows || []).filter(r => Number(r.amount_ex_gst || 0) > 0).length;

  async function doGenerate() {
    if (!window.confirm(
      `Generate pending payouts for ${partnerCount} partner(s) covering ${periodLabel(period)}?\n\n` +
      `Total ex-GST across partners: ${money(totalEx)}\n\n` +
      `Idempotent — if a pending payout already exists for a partner it tops it up instead of creating a duplicate.`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/commissions/payouts/generate-monthly", null,
        { params: { period, dry_run: false } });
      setGenerated(data);
      setStage(3);
      toast.success(`Generated ${data.generated.length} payout(s).`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Generate failed");
    } finally { setBusy(false); }
  }

  async function doEmailAll() {
    const payouts = generated?.generated || [];
    if (!payouts.length) {
      toast.error("No payouts to email");
      return;
    }
    if (!window.confirm(
      `Email the ${payouts.length} partner statement(s) now?\n\n` +
      `Each partner gets their own branded PDF. Any that are already attached to a paid payout are skipped.`
    )) return;
    setBusy(true);
    const results = [];
    try {
      for (const p of payouts) {
        try {
          const r = await api.post(`/commissions/payouts/${p.payout_id}/send-email`);
          results.push({ partner: p.partner_code, ok: true, to: r.data.to });
        } catch (e) {
          results.push({ partner: p.partner_code, ok: false,
                          error: formatApiErrorDetail(e.response?.data?.detail) || "failed" });
        }
      }
      setEmailResult(results);
      setStage(4);
      const okCount = results.filter(r => r.ok).length;
      toast.success(`Emailed ${okCount}/${results.length} partners.`);
      if (onDone) onDone();
    } catch (e) {
      toast.error("Email batch failed unexpectedly");
    } finally { setBusy(false); }
  }

  const healthOk = strip?.overall_status === "green";
  const stageNames = ["Health check", "Reconcile preview", "Generate payouts", "Email partners", "Done"];

  return (
    <div
      data-testid="finalise-wizard-overlay"
      className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto"
      onClick={onClose}
    >
      <div
        data-testid="finalise-wizard"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl my-10 mx-4"
      >
        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-slate-800">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-cyan-400 font-semibold mb-1">
              Finalise month
            </div>
            <h3 className="font-display text-2xl">
              {periodLabel(period)} — {stageNames[stage]}
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              Step {stage + 1} of 5 · each step confirms before anything changes
            </p>
          </div>
          <button
            data-testid="finalise-wizard-close"
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-slate-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress dots */}
        <div className="flex items-center gap-2 px-6 py-4 border-b border-slate-800 overflow-x-auto">
          {stageNames.map((name, i) => (
            <div key={name} className="flex items-center gap-2 shrink-0">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold border ${
                i < stage ? "bg-cyan-500 border-cyan-400 text-slate-950" :
                i === stage ? "border-cyan-500 text-cyan-400" :
                "border-slate-800 text-slate-600"
              }`}>
                {i < stage ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <span className={`text-xs ${i === stage ? "text-slate-100 font-semibold" : "text-slate-500"}`}>
                {name}
              </span>
              {i < stageNames.length - 1 && (
                <span className="text-slate-700 text-[10px] mx-1">→</span>
              )}
            </div>
          ))}
        </div>

        <div className="px-6 py-5 space-y-4 min-h-[240px]">
          {resumeNotice && (
            <div
              data-testid="finalise-resume-notice"
              className="rounded-lg border border-cyan-500/40 bg-cyan-500/5 px-4 py-3 flex items-start justify-between gap-3"
            >
              <div className="text-xs text-cyan-200/90 leading-relaxed">
                <strong>Resumed from where you left off.</strong>{" "}
                You were on step <span className="font-mono">{stage + 1}</span> ({stageNames[stage]}).
                Click <em>Back</em> to review earlier steps or <em>Start over</em> to reset this period.
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  data-testid="finalise-restart"
                  onClick={() => {
                    try { localStorage.removeItem(storageKey); } catch {}
                    setStage(0); setGenerated(null); setEmailResult(null);
                    setResumeNotice(false);
                  }}
                  className="text-[11px] text-cyan-300 hover:text-cyan-100 underline underline-offset-4"
                >
                  Start over
                </button>
                <button
                  type="button"
                  data-testid="finalise-dismiss-resume"
                  onClick={() => setResumeNotice(false)}
                  className="p-1 rounded text-cyan-300 hover:bg-cyan-500/15"
                  aria-label="Dismiss resume notice"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
          {stage === 0 && (
            <div className="space-y-3" data-testid="finalise-stage-health">
              <p className="text-sm text-slate-300">
                Checking the ledger before we touch any payouts.
              </p>
              {!strip ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading health status…
                </div>
              ) : (
                <div className={`rounded-lg border p-4 ${healthOk ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"}`}>
                  <div className="flex items-center gap-2 text-sm font-semibold mb-2">
                    {healthOk ? (
                      <><CheckCircle2 className="w-4 h-4 text-emerald-400" /><span className="text-emerald-300">Ledger is healthy — safe to proceed</span></>
                    ) : (
                      <><AlertTriangle className="w-4 h-4 text-amber-300" /><span className="text-amber-300">Some checks need review before finalising</span></>
                    )}
                  </div>
                  <ul className="space-y-1 text-xs">
                    {strip.dots.map(d => (
                      <li key={d.label} className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${d.status === "green" ? "bg-emerald-400" : d.status === "amber" ? "bg-amber-400" : d.status === "red" ? "bg-rose-500" : "bg-slate-500"}`} />
                        <span className="text-slate-300 min-w-[90px]">{d.label}</span>
                        <span className="text-slate-500">{d.detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {stage === 1 && (
            <div className="space-y-3" data-testid="finalise-stage-preview">
              <p className="text-sm text-slate-300">
                Here's what each partner is owed for {periodLabel(period)}. Review before generating.
              </p>
              {!preview ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading reconcile preview…
                </div>
              ) : (preview.rows || []).length === 0 ? (
                <div className="rounded-lg border border-slate-800 bg-slate-950 p-6 text-center text-sm text-slate-500">
                  No un-paid accrued events for this period. Nothing to finalise.
                </div>
              ) : (
                <div className="rounded-lg border border-slate-800 bg-slate-950 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-900 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                      <tr>
                        <th className="text-left px-4 py-2">Partner</th>
                        <th className="text-right px-4 py-2">Events</th>
                        <th className="text-right px-4 py-2">Ex-GST</th>
                        <th className="text-right px-4 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800 font-mono text-xs">
                      {preview.rows.map(r => (
                        <tr key={r.partner_code}>
                          <td className="px-4 py-2 text-slate-200">
                            {r.partner_name} <span className="text-slate-500">· {r.partner_code}</span>
                          </td>
                          <td className="px-4 py-2 text-right text-slate-300">{r.event_count || 0}</td>
                          <td className="px-4 py-2 text-right text-cyan-300">{money(r.amount_ex_gst || 0)}</td>
                          <td className="px-4 py-2 text-right text-slate-500">
                            {Number(r.amount_ex_gst || 0) > 0 ? "ready" : "no events"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-900/60 font-mono text-xs">
                      <tr>
                        <td className="px-4 py-2 text-slate-400 font-semibold uppercase tracking-wider">Total</td>
                        <td className="px-4 py-2 text-right text-slate-300">—</td>
                        <td className="px-4 py-2 text-right text-cyan-300 font-semibold">{money(totalEx)}</td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {stage === 2 && (
            <div className="space-y-3" data-testid="finalise-stage-generate">
              <p className="text-sm text-slate-300">
                Ready to generate <b className="text-cyan-300">{partnerCount}</b> pending payout(s) totalling <b className="text-cyan-300">{money(totalEx)} ex-GST</b> for {periodLabel(period)}.
              </p>
              <ul className="text-xs text-slate-400 space-y-1 list-disc pl-5">
                <li>Idempotent — tops up existing pending payouts, no duplicates</li>
                <li>Partners with no un-paid events are skipped</li>
                <li>Nothing is emailed yet — that's the next step</li>
              </ul>
            </div>
          )}

          {stage === 3 && (
            <div className="space-y-3" data-testid="finalise-stage-email">
              <p className="text-sm text-slate-300">
                Generated <b className="text-cyan-300">{(generated?.generated || []).length}</b> payout(s). Send them now?
              </p>
              {generated && (
                <ul className="text-xs text-slate-400 space-y-1 max-h-48 overflow-y-auto font-mono">
                  {generated.generated.map(g => (
                    <li key={g.payout_id} className="flex justify-between border-b border-slate-800 py-1">
                      <span className="text-slate-300">{g.partner_name} <span className="text-slate-500">· {g.partner_code}</span></span>
                      <span className="text-cyan-300">{money(g.amount_total || 0)} ({g.event_count} evt)</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {stage === 4 && (
            <div className="space-y-3" data-testid="finalise-stage-done">
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4">
                <div className="flex items-center gap-2 text-emerald-300 text-sm font-semibold mb-2">
                  <CheckCircle2 className="w-4 h-4" /> Month finalised
                </div>
                <ul className="text-xs text-slate-400 space-y-1 font-mono">
                  {(emailResult || []).map(r => (
                    <li key={r.partner} className="flex justify-between">
                      <span>{r.partner}</span>
                      <span className={r.ok ? "text-emerald-300" : "text-rose-300"}>
                        {r.ok ? `→ ${r.to}` : `✗ ${r.error}`}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-800">
          <button
            data-testid="finalise-wizard-back"
            type="button"
            onClick={() => stage > 0 && stage < 4 && setStage(stage - 1)}
            disabled={stage === 0 || stage >= 4 || busy}
            className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Back
          </button>
          <div className="flex items-center gap-2">
            {stage < 2 && (
              <button
                data-testid="finalise-wizard-next"
                type="button"
                onClick={() => setStage(stage + 1)}
                disabled={busy || (stage === 1 && (!preview || (preview.rows || []).length === 0))}
                className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-40 text-white px-5 py-2 rounded-lg text-sm font-semibold"
              >
                Continue
              </button>
            )}
            {stage === 2 && (
              <button
                data-testid="finalise-wizard-generate"
                type="button"
                onClick={doGenerate}
                disabled={busy || partnerCount === 0}
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-slate-950 px-5 py-2 rounded-lg text-sm font-semibold"
              >
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : "Generate payouts"}
              </button>
            )}
            {stage === 3 && (
              <button
                data-testid="finalise-wizard-email"
                type="button"
                onClick={doEmailAll}
                disabled={busy || !(generated?.generated || []).length}
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-950 px-5 py-2 rounded-lg text-sm font-semibold"
              >
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Emailing…</> : <><Mail className="w-4 h-4" /> Email all partners</>}
              </button>
            )}
            {stage === 4 && (
              <button
                data-testid="finalise-wizard-close-done"
                type="button"
                onClick={onClose}
                className="bg-slate-800 hover:bg-slate-700 text-slate-100 px-5 py-2 rounded-lg text-sm font-semibold"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
